import type { ChartArtifacts, ChartManifest, PlotTrace, SeriesStatistics } from "../../../shared/charts/manifest.ts";
import { MODELS, modelTraces, teachingModel, type ModelResult, type TeachingModel } from "../../../shared/charts/models.ts";

/**
 * What the chat's chart renderer needs besides drawing: reading a `show_chart` result, the plot
 * copy Plotly may draw and mutate, and the download list. Kept free of JSX so `node --test` can load it.
 */

export type ChartResult =
	| { state: "running" }
	| { state: "failed"; message: string }
	| { state: "ready"; chart: string; artifacts: ChartArtifacts };

/** Reads a `show_chart` call's result: still running, failed with its reason, or the saved chart's paths. */
export function readChartResult(result: { content?: unknown; details?: unknown } | undefined): ChartResult {
	if (!result) return { state: "running" };
	const details = isObject(result.details) ? result.details : undefined;
	if (details?.ok === true && typeof details.chart === "string" && isObject(details.artifacts)) {
		return { state: "ready", chart: details.chart, artifacts: details.artifacts as unknown as ChartArtifacts };
	}
	if (details?.ok === false && typeof details.message === "string") {
		return { state: "failed", message: typeof details.code === "string" ? `${details.code}: ${details.message}` : details.message };
	}
	return { state: "failed", message: textOf(result.content) || "show_chart returned no chart." };
}

/** The saved manifest when it has the shape the renderer reads, else why not: the file is read back from the workspace. */
export function readManifest(value: unknown): { ok: true; manifest: ChartManifest } | { ok: false; message: string } {
	const fail = (what: string) => ({ ok: false as const, message: `The saved chart ${what}.` });
	if (!isObject(value) || value.version !== 1) return fail("is not a version-1 chart manifest");
	const plot = value.plot;
	if (typeof value.title !== "string" || !isObject(value.provenance) || typeof value.provenance.kind !== "string") return fail("has no title or provenance");
	if (!isObject(plot) || !Array.isArray(plot.data) || !plot.data.every(isObject) || !isObject(plot.layout)) return fail("has no plot");
	if (!isStrings(value.explanation) || !isStrings(value.warnings) || !Array.isArray(value.statistics)) return fail("has no explanation, warnings or statistics");
	return { ok: true, manifest: value as unknown as ChartManifest };
}

/** The trace types `show_chart` compiles to; a manifest naming any other is not drawn. */
const TRACE_TYPES = new Set(["bar", "box", "contour", "heatmap", "histogram", "parcoords", "pie", "scatter", "scatter3d", "scatterpolar", "surface", "violin"]);

/** Layout keys that load images or restyle from outside the chart; a saved chart never sets them. */
const DROPPED_LAYOUT = ["images", "template"];

/** The renderer's own Plotly config: local interactions only, the modebar always shown. */
export const PLOT_CONFIG = {
	responsive: true,
	displaylogo: false,
	displayModeBar: true,
	scrollZoom: false,
	showLink: false,
	showSendToCloud: false,
} as const;

export interface DrawnPlot {
	data: PlotTrace[];
	layout: Record<string, unknown>;
}

/** Room above the plot for the modebar, once the title is shown outside it. */
const TOP_MARGIN = 36;

/**
 * A fresh copy of a saved plot for Plotly, which writes visibility and view into what it is given;
 * drawing a new copy is how reset returns to the saved chart. The chat shows the title above the
 * plot, so the copy leaves Plotly's out. Throws on a trace type `show_chart` never writes, since
 * the file is read back from the workspace.
 */
export function drawablePlot(plot: Pick<ChartManifest["plot"], "data" | "layout">): DrawnPlot {
	const unknown = plot.data.find((trace) => !TRACE_TYPES.has(trace.type));
	if (unknown) throw new Error(`This chart has a "${String(unknown.type)}" trace, which the chat does not draw.`);
	const layout = structuredClone(plot.layout);
	for (const key of [...DROPPED_LAYOUT, "title"]) delete layout[key];
	layout.margin = { ...(isObject(layout.margin) ? layout.margin : {}), t: TOP_MARGIN };
	return { data: structuredClone(plot.data), layout };
}

type SavedModel = NonNullable<ChartManifest["model"]>;

/** One labeled control of a model chart: a distribution parameter or the sample size. */
export interface ModelControl {
	name: string;
	label: string;
}

/** The controls of a saved model, parameters in catalog order, then the sample size. */
export function modelControls(model: SavedModel): ModelControl[] {
	const description = MODELS.find((m) => m.distribution === model.distribution);
	const parameters = description ? description.parameters.map((p) => ({ name: p.name, label: p.label })) : [];
	return [...parameters, { name: "sampleSize", label: "Sample size" }];
}

/** The control values a saved model starts with, as the text the inputs show. */
export function modelInputs(model: SavedModel): Record<string, string> {
	return Object.fromEntries(modelControls(model).map((c) => [c.name, String(c.name === "sampleSize" ? model.sampleSize : model.parameters[c.name])]));
}

/** The model the edited inputs describe, sampled with the saved seed; a blank input is invalid. */
export function editedModel(model: SavedModel, inputs: Record<string, string>): ModelResult {
	const number = (name: string) => (inputs[name]?.trim() ? Number(inputs[name]) : Number.NaN);
	const parameters = Object.fromEntries(modelControls(model).filter((c) => c.name !== "sampleSize").map((c) => [c.name, number(c.name)]));
	return teachingModel({ distribution: model.distribution, parameters, sampleSize: number("sampleSize"), seed: model.seed });
}

/** The saved traces with the model curve and samples redrawn from `model`; other traces stay as saved. */
export function withModel(data: PlotTrace[], model: TeachingModel): PlotTrace[] {
	const fresh = modelTraces(model);
	return data.map((trace) => {
		const next = fresh.find((t) => t.meta?.role !== undefined && t.meta.role === trace.meta?.role);
		return next ? { ...trace, ...next } : trace;
	});
}

export interface StatisticsTable {
	head: string[];
	rows: string[][];
	/** What else was calculated per series: bins, a fit, a bandwidth, observations removed. */
	notes: string[];
}

/** Outliers listed per series before the rest are only counted. */
const LISTED_OUTLIERS = 10;

/** The calculated statistics as text cells; a statistic that does not apply reads "-", an undefined one "undefined". */
export function statisticsTable(all: SeriesStatistics[]): StatisticsTable {
	const head = ["Series", "n", "Mean", "SD (n - 1)", "Q1", "Median", "Q3", "IQR", "Lower fence", "Upper fence", "Outliers"];
	const rows = all.map((s) => {
		const summary = s.mean === undefined ? new Array<string>(8).fill("-") : [s.mean, s.sampleStandardDeviation, s.q1, s.median, s.q3, s.iqr, s.lowerFence, s.upperFence].map(stat);
		return [s.name, formatNumber(s.n), ...summary, s.outliers ? outliers(s.outliers) : "-"];
	});
	return { head, rows, notes: all.flatMap(notes) };
}

function notes(s: SeriesStatistics): string[] {
	const out: string[] = [];
	if (s.removed) out.push(`${s.name}: ${s.removed} observation${s.removed > 1 ? "s" : ""} with a missing value removed.`);
	if (s.bins) {
		const { edges, counts, rule } = s.bins;
		const bins = counts.map((c, i) => `[${formatNumber(edges[i]!)}, ${formatNumber(edges[i + 1]!)}${i === counts.length - 1 ? "]" : ")"}: ${c}`);
		out.push(`${s.name} bins (${rule}): ${bins.join("; ")}.`);
	}
	if (s.bins2d) out.push(`${s.name}: ${s.bins2d.xEdges.length - 1} x ${s.bins2d.yEdges.length - 1} bins (${s.bins2d.rule}).`);
	if (s.bandwidth) out.push(`${s.name}: kernel bandwidth ${formatNumber(s.bandwidth.value)} (${s.bandwidth.rule}).`);
	if (s.fit) out.push(`${s.name}: least-squares line y = ${formatNumber(s.fit.slope)} x + ${formatNumber(s.fit.intercept)}. It describes this data; it does not show cause.`);
	return out;
}

function outliers(values: number[]): string {
	if (!values.length) return "none";
	const listed = values.slice(0, LISTED_OUTLIERS).map(formatNumber).join(", ");
	return values.length > LISTED_OUTLIERS ? `${listed} and ${values.length - LISTED_OUTLIERS} more` : listed;
}

function stat(value: number | null | undefined): string {
	return value == null ? "undefined" : formatNumber(value);
}

/** Six significant digits. */
export function formatNumber(value: number): string {
	return Number.isInteger(value) ? String(value) : String(Number(value.toPrecision(6)));
}

export interface ChartDownload {
	label: string;
	path: string;
}

/** The saved outputs offered for download, in the order the chart lists them. */
export function chartDownloads(artifacts: ChartArtifacts): ChartDownload[] {
	const downloads: [string, string][] = [
		["PNG", artifacts.png],
		["SVG", artifacts.svg],
		["CSV", artifacts.csv],
		["Static HTML", artifacts.html],
		["JSON", artifacts.json],
	];
	return downloads.filter(([, path]) => typeof path === "string" && path !== "").map(([label, path]) => ({ label, path }));
}

function isObject(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isStrings(value: unknown): value is string[] {
	return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function textOf(content: unknown): string {
	if (!Array.isArray(content)) return "";
	return content
		.map((part) => (isObject(part) && part.type === "text" && typeof part.text === "string" ? part.text : ""))
		.join("")
		.trim();
}
